import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getSelectedOrganizationInfo } from '../shared-state';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import {
  IBulkOrderStatusRequest,
  ICreateZraInvoiceRequest,
  IZraInvoiceStatusResponse,
  ZraInvoiceService,
} from '@boma/supplier-portal/shared/services';
export interface ZraInvoiceError {
  message: string;
  status?: number;
  timestamp?: string;
}

export interface ZraInvoiceState {
  invoiceStatuses: Map<string, IZraInvoiceStatusResponse>;
  loading: boolean;
  submitLoading: boolean;
  bulkStatusLoading: boolean;
  error: ZraInvoiceError | null;
}

@Injectable()
export class ZraInvoiceFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<ZraInvoiceState> =
    new BehaviorSubject<ZraInvoiceState>({
      invoiceStatuses: new Map(),
      loading: false,
      submitLoading: false,
      bulkStatusLoading: false,
      error: null,
    });

  getInvoiceStatus$(
    orderId: string
  ): Observable<IZraInvoiceStatusResponse | undefined> {
    return this._state$.pipe(
      map((state) => state.invoiceStatuses.get(orderId))
    );
  }

  invoiceStatuses$: Observable<IZraInvoiceStatusResponse[]> = this._state$.pipe(
    map((state) => Array.from(state.invoiceStatuses.values()))
  );

  loading$: Observable<boolean> = this._state$.pipe(
    map((state) => state.loading)
  );

  submitLoading$: Observable<boolean> = this._state$.pipe(
    map((state) => state.submitLoading)
  );

  bulkStatusLoading$: Observable<boolean> = this._state$.pipe(
    map((state) => state.bulkStatusLoading)
  );

  error$: Observable<ZraInvoiceError | null> = this._state$.pipe(
    map((state) => state.error)
  );

  constructor(
    private store: Store,
    private zraInvoiceService: ZraInvoiceService
  ) {
    this._entityId$ = this.store
      .select(getSelectedOrganizationInfo)
      .pipe(map((info) => info.id));
  }

  getSubmissionStatus(orderId: string) {
    console.log('🚀 ZraInvoiceFacade.getSubmissionStatus called for orderId:', orderId);
    this._updateState({ loading: true, error: null });

    return this._entityId$
      .pipe(
        switchMap((entityId) => {
          console.log('📡 Making API call to ZRA service:', { entityId, orderId });
          return this.zraInvoiceService.getSubmissionStatus(entityId, orderId).pipe(
            tap((status) => {
              console.log('✅ ZRA API Response received:', status);
            }),
            catchError((error) => {
              console.error('❌ ZRA API Error:', error);
              this._handleError(
                'Failed to get invoice submission status',
                error
              );
              return of(null);
            })
          );
        }),
        finalize(() => {
          console.log('🏁 ZRA API call finalized');
          this._updateState({ loading: false });
        })
      )
      .subscribe({
        next: (status) => {
          if (status) {
            console.log('💾 Updating invoice status in state:', status);
            this._updateInvoiceStatuses(status);
          } else {
            console.log('⚠️ No status received (possibly error or not found)');
          }
        },
      });
  }

  getDebitNoteSubmissionStatus(orderId: string) {
    this._updateState({ loading: true, error: null });

    return this._entityId$
      .pipe(
        switchMap((entityId) =>
          this.zraInvoiceService.getDebitNoteSubmissionStatus(entityId, orderId).pipe(
            catchError((error) => {
              this._handleError(
                'Failed to get debit note submission status',
                error
              );
              return of(null);
            })
          )
        ),
        finalize(() => this._updateState({ loading: false }))
      )
      .subscribe({
        next: (status) => {
          if (status) {
            this._updateInvoiceStatuses(status);
          }
        },
      });
  }

  getSubmissionStatuses(
    request: IBulkOrderStatusRequest
  ): Observable<IZraInvoiceStatusResponse[] | null> {
    this._updateState({ bulkStatusLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService.getSubmissionStatuses(entityId, request).pipe(
          tap((statuses) => {
            if (statuses) {
              statuses.forEach((status) => this._updateInvoiceStatuses(status));
            }
          }),
          catchError((error) => {
            this._handleError(
              'Failed to get bulk invoice submission statuses',
              error
            );
            this._updateState({ bulkStatusLoading: false });
            return of(null);
          }),
          finalize(() => this._updateState({ bulkStatusLoading: false }))
        )
      )
    );
  }

  submitOrderInvoice(
    orderId: string,
    request: ICreateZraInvoiceRequest
  ): Observable<IZraInvoiceStatusResponse | null> {
    this._updateState({ submitLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService
          .submitOrderInvoice(entityId, orderId, request)
          .pipe(
            tap((status) => {
              if (status) {
                this._updateInvoiceStatuses(status);
              }
            }),
            catchError((error) => {
              this._handleError('Failed to submit order invoice to ZRA', error);
              this._updateState({ submitLoading: false });
              return of(null);
            }),
            finalize(() => this._updateState({ submitLoading: false }))
          )
      )
    );
  }

  queueSync(request: IBulkOrderStatusRequest): Observable<string | null> {
    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService.queueSync(entityId, request).pipe(
          catchError((error) => {
            this._handleError('Failed to queue ZRA sync', error);
            return of(null);
          })
        )
      )
    );
  }

  /**
   * Get the ZRA invoice PDF for a specific order
   * @param orderId The ID of the order
   * @returns Observable of blob data for the PDF
   */
  getInvoicePdf(orderId: string): Observable<Blob | null> {
    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService.getInvoicePdf(entityId, orderId).pipe(
          catchError((error) => {
            this._handleError('Failed to download ZRA invoice PDF', error);
            return of(null);
          })
        )
      )
    );
  }

  /**
   * Get the ZRA credit note PDF for a specific order
   * @param orderId The ID of the order
   * @returns Observable of blob data for the PDF
   */
  getCreditNotePdf(orderId: string): Observable<Blob | null> {
    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService.getCreditNotePdf(entityId, orderId).pipe(
          catchError((error) => {
            this._handleError('Failed to download ZRA credit note PDF', error);
            return of(null);
          })
        )
      )
    );
  }

  /**
   * Submit an order debit note to ZRA
   */
  submitOrderDebitNote(
    orderId: string,
    request: ICreateZraInvoiceRequest
  ): Observable<IZraInvoiceStatusResponse | null> {
    this._updateState({ submitLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService
          .submitOrderDebitNote(entityId, orderId, request)
          .pipe(
            tap((status) => {
              if (status) {
                this._updateInvoiceStatuses(status);
              }
            }),
            catchError((error) => {
              this._handleError('Failed to submit order debit note to ZRA', error);
              this._updateState({ submitLoading: false });
              return of(null);
            }),
            finalize(() => this._updateState({ submitLoading: false }))
          )
      )
    );
  }

  /**
   * Get the ZRA debit note PDF for a specific order
   * @param orderId The ID of the order
   * @returns Observable of blob data for the PDF
   */
  getDebitNotePdf(orderId: string): Observable<Blob | null> {
    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraInvoiceService.getDebitNotePdf(entityId, orderId).pipe(
          catchError((error) => {
            this._handleError('Failed to download ZRA debit note PDF', error);
            return of(null);
          })
        )
      )
    );
  }

  private _updateInvoiceStatuses(status: IZraInvoiceStatusResponse): void {
    const updatedMap = new Map(this._state$.value.invoiceStatuses);
    updatedMap.set(status.orderId, status);
    this._updateState({
      invoiceStatuses: updatedMap,
      loading: false,
      submitLoading: false,
      bulkStatusLoading: false,
    });
  }

  private _updateState(state: Partial<ZraInvoiceState>): void {
    this._state$.next({ ...this._state$.value, ...state });
  }

  private _handleError(context: string, error: any): void {
    // Extract the actual error message from the server response
    const serverMessage = error?.error?.message || error?.error?.title || error?.error;
    const statusCode = error?.status || 'Unknown';
    
    console.error(`${context}:`, {
      message: serverMessage,
      status: statusCode,
      fullError: error,
    });

    const errorObj: ZraInvoiceError = {
      message: serverMessage || error.message || context,
      status: error.status,
      timestamp: new Date().toISOString(),
    };

    this._updateState({ error: errorObj, loading: false });
  }
}
